import React, { useState, useEffect, useCallback } from "react";
import {
  Calculator,
  FileText,
  DollarSign,
  Plus,
  Minus,
  Edit,
  Save,
  X,
  Check,
  AlertTriangle,
  RefreshCw,
  Download,
  Eye,
  Search,
  Filter,
  Calendar,
  Building,
  User,
  Receipt,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Percent,
  Target,
  Activity,
} from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import ClampedText from "../../components/common/ClampedText.jsx";
import {
  getFinancialApplications,
  processDischargeSettlement,
} from "../../services/financialservice";
import { generateLetter } from "../../services/applicationService";

const DischargeSettlement = () => {
  const { userData: user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null); // applicationId being generated
  const [previewUrl, setPreviewUrl] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [settlementData, setSettlementData] = useState({
    status: "approved", // approved, rejected, pending_adjustment
    dischargeItems: [],
    totalActualCost: 0,
    approvedAmount: 0,
    deductions: [],
    finalSettlementAmount: 0,
    adjustments: [],
    comments: "",
  });

  // Load applications with discharge summaries ready for settlement
  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      // Filter for applications that have discharge summaries and are ready for settlement
      const filters = {
        searchTerm,
        // status intentionally omitted to include both medical_approved and approved_for_process (and similar)
        ...(statusFilter && { settlementStatus: statusFilter }),
      };

  const result = await getFinancialApplications({ ...filters, forSettlement: true }, 1, 50);
      if (result.success) {
        // Filter for applications that have discharge summaries
        const applicationsWithDischarge = result.applications.filter(
          (app) =>
    app.workflow?.medicalVerification?.dischargeReviewCompleted
        );
        setApplications(applicationsWithDischarge);
      }
    } catch (error) {
      console.error("Error loading applications:", error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const openCalculator = (application) => {
    setSelectedApplication(application);

    // Initialize settlement data from existing discharge review
    const dischargeReview =
      application.workflow?.medicalVerification?.dischargeReview;
    const finalApprovedAmount =
      application.workflow?.medicalVerification?.finalApprovedAmount || 0;

    if (dischargeReview) {
      setSettlementData({
        status: "approved",
        dischargeItems: dischargeReview.items || [],
        totalActualCost: dischargeReview.totalAmount || 0,
        approvedAmount: finalApprovedAmount,
        deductions: [],
        finalSettlementAmount: finalApprovedAmount,
        adjustments: [],
        comments: "",
      });
    } else {
      // Initialize with basic data
      setSettlementData({
        status: "approved",
        dischargeItems: [],
        totalActualCost: application.medical?.actualCost || 0,
        approvedAmount: finalApprovedAmount,
        deductions: [],
        finalSettlementAmount: finalApprovedAmount,
        adjustments: [],
        comments: "",
      });
    }

    setCalculatorOpen(true);
  };

  const addDeduction = () => {
    setSettlementData((prev) => ({
      ...prev,
      deductions: [
        ...prev.deductions,
        {
          id: Date.now(),
          type: "excess_billing",
          description: "",
          amount: 0,
          percentage: false,
        },
      ],
    }));
  };

  const updateDeduction = (id, field, value) => {
    setSettlementData((prev) => ({
      ...prev,
      deductions: prev.deductions.map((deduction) =>
        deduction.id === id ? { ...deduction, [field]: value } : deduction
      ),
    }));
  };

  const removeDeduction = (id) => {
    setSettlementData((prev) => ({
      ...prev,
      deductions: prev.deductions.filter((deduction) => deduction.id !== id),
    }));
  };

  const addAdjustment = () => {
    setSettlementData((prev) => ({
      ...prev,
      adjustments: [
        ...prev.adjustments,
        {
          id: Date.now(),
          type: "policy_limit",
          description: "",
          amount: 0,
          reason: "",
        },
      ],
    }));
  };

  const updateAdjustment = (id, field, value) => {
    setSettlementData((prev) => ({
      ...prev,
      adjustments: prev.adjustments.map((adjustment) =>
        adjustment.id === id ? { ...adjustment, [field]: value } : adjustment
      ),
    }));
  };

  const removeAdjustment = (id) => {
    setSettlementData((prev) => ({
      ...prev,
      adjustments: prev.adjustments.filter(
        (adjustment) => adjustment.id !== id
      ),
    }));
  };

  const calculateFinalAmount = () => {
    let totalDeductions = 0;
    let totalAdjustments = 0;

    // Calculate deductions
    settlementData.deductions.forEach((deduction) => {
      if (deduction.percentage) {
        totalDeductions +=
          (settlementData.approvedAmount * deduction.amount) / 100;
      } else {
        totalDeductions += parseFloat(deduction.amount || 0);
      }
    });

    // Calculate adjustments
    settlementData.adjustments.forEach((adjustment) => {
      totalAdjustments += parseFloat(adjustment.amount || 0);
    });

    const finalAmount =
      settlementData.approvedAmount - totalDeductions + totalAdjustments;
    return Math.max(0, finalAmount);
  };

  const handleSubmit = async () => {
    if (!selectedApplication || !user?.uid) return;

    setSubmitting(true);
    try {
      const finalSettlementData = {
        ...settlementData,
        finalSettlementAmount: calculateFinalAmount(),
      };

      const result = await processDischargeSettlement(
        selectedApplication.applicationId,
        user.uid,
        finalSettlementData
      );

      if (result.success) {
        setCalculatorOpen(false);
        setSelectedApplication(null);
        loadApplications(); // Refresh the list
        alert("Settlement processed successfully!");
      } else {
        alert("Error processing settlement: " + result.error);
      }
    } catch (error) {
      console.error("Error submitting settlement:", error);
      alert("Error submitting settlement");
    } finally {
      setSubmitting(false);
    }
  };

  const ApplicationCard = ({ application }) => {
    const dischargeReview =
      application.workflow?.medicalVerification?.dischargeReview;
    const finalApprovedAmount =
      application.workflow?.medicalVerification?.finalApprovedAmount || 0;
    const settlementStatus = application.workflow?.dischargeSettlement?.status;

  const handleGenerateApproval = async () => {
      if (!application?.applicationId) return;
      try {
        setGeneratingFor(application.applicationId);
        const res = await generateLetter(application.applicationId, "approval");
        if (res?.success) {
      // Show inline preview modal
      if (res.letterUrl) setPreviewUrl(res.letterUrl);
        } else {
          alert("Failed to generate letter: " + (res?.error || "Unknown error"));
        }
      } catch (e) {
        console.error("Error generating approval letter", e);
        alert("Error generating approval letter");
      } finally {
        setGeneratingFor(null);
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <h3 className="text-lg font-semibold text-gray-900">
                {application.applicationId}
              </h3>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  settlementStatus === "approved"
                    ? "bg-green-100 text-green-800"
                    : settlementStatus === "pending_adjustment"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {settlementStatus || "Ready for Settlement"}
              </span>
              {application.emergency && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Emergency
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Patient & Hospital Info */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <User className="h-4 w-4 mr-2 text-teal-600" />
                  Patient Details
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>
                    <strong>Name:</strong> {application.patient?.name}
                  </div>
                  <div>
                    <strong>Age:</strong> {application.patient?.age} years
                  </div>
                  <div>
                    <strong>Hospital:</strong> {application.hospitalName}
                  </div>
                  <div>
                    <strong>Diagnosis:</strong>{" "}
                    {application.medical?.diagnosis || "Not specified"}
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <DollarSign className="h-4 w-4 mr-2 text-teal-600" />
                  Financial Summary
                </h4>
                <div className="text-sm space-y-1">
                  <div className="text-gray-600">
                    <strong>Actual Cost:</strong>{" "}
                    {formatCurrency(application.medical?.actualCost)}
                  </div>
                  <div className="text-blue-600">
                    <strong>Doctor Approved:</strong>{" "}
                    {formatCurrency(finalApprovedAmount)}
                  </div>
                  {dischargeReview && (
                    <div className="text-green-600">
                      <strong>Items Verified:</strong>{" "}
                      {dischargeReview.items?.length || 0}
                    </div>
                  )}
                  {application.workflow?.dischargeSettlement
                    ?.finalSettlementAmount && (
                    <div className="text-purple-600 font-medium">
                      <strong>Final Settlement:</strong>{" "}
                      {formatCurrency(
                        application.workflow.dischargeSettlement
                          .finalSettlementAmount
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Discharge Info */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <Receipt className="h-4 w-4 mr-2 text-teal-600" />
                  Discharge Status
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>
                    <strong>Review Status:</strong>
                    <span className="text-green-600 ml-1">Completed</span>
                  </div>
                  <div>
                    <strong>Discharge Date:</strong>{" "}
                    {formatDate(application.medical?.dischargeDate)}
                  </div>
                  <div>
                    <strong>Policy:</strong>{" "}
                    {application.policy?.insuranceCompany}
                  </div>
                  <div>
                    <strong>Submitted:</strong>{" "}
                    {formatDate(application.submittedAt)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col space-y-2 ml-4">
            {!application.workflow?.dischargeSettlement?.status && (
              <button
                onClick={() => openCalculator(application)}
                className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm"
              >
                <Calculator className="h-4 w-4" />
                <span>Calculate Settlement</span>
              </button>
            )}

            <button
              onClick={() => setSelectedApplication(application)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
            >
              <Eye className="h-4 w-4" />
              <span>View Details</span>
            </button>

            {application.workflow?.dischargeSettlement?.status && (
              <button
                onClick={() => openCalculator(application)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Settlement</span>
              </button>
            )}

            {/* Generate Final Approval Letter */}
            <button
              onClick={handleGenerateApproval}
              disabled={settlementStatus !== "approved" || generatingFor === application.applicationId}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors text-sm ${
                settlementStatus === "approved" && generatingFor !== application.applicationId
                  ? "bg-purple-600 text-white hover:bg-purple-700"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
              }`}
              title={
                settlementStatus === "approved"
                  ? "Generate Final Approval Letter"
                  : "Available after settlement is approved"
              }
            >
              <FileText className="h-4 w-4" />
              <span>
                {generatingFor === application.applicationId
                  ? "Generating..."
                  : "Generate Final Approval Letter"}
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const SettlementCalculator = () => {
    if (!selectedApplication) return null;

    const finalAmount = calculateFinalAmount();
    const totalDeductions = settlementData.deductions.reduce(
      (sum, deduction) => {
        if (deduction.percentage) {
          return sum + (settlementData.approvedAmount * deduction.amount) / 100;
        }
        return sum + parseFloat(deduction.amount || 0);
      },
      0
    );

    const totalAdjustments = settlementData.adjustments.reduce(
      (sum, adjustment) => sum + parseFloat(adjustment.amount || 0),
      0
    );

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Discharge Settlement Calculator
              </h2>
              <button
                onClick={() => setCalculatorOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Application ID: {selectedApplication.applicationId} | Patient:{" "}
              {selectedApplication.patient?.name}
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Settlement Summary */}
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Settlement Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {formatCurrency(settlementData.approvedAmount)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Doctor Approved Amount
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    -{formatCurrency(totalDeductions)}
                  </div>
                  <div className="text-sm text-gray-600">Total Deductions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    +{formatCurrency(totalAdjustments)}
                  </div>
                  <div className="text-sm text-gray-600">Total Adjustments</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">
                    {formatCurrency(finalAmount)}
                  </div>
                  <div className="text-sm text-gray-600">Final Settlement</div>
                </div>
              </div>
            </div>

            {/* Discharge Items Review */}
            {settlementData.dischargeItems.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Discharge Items Breakdown
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 text-sm font-medium text-gray-700">Item/Service</th>
                        <th className="text-left py-2 text-sm font-medium text-gray-700">Category</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-700">Qty</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-700">Rate/Unit</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-700">Eligible Rate/Unit</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-700">Billed Amount</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-700">Eligible Amount</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-700">Variance</th>
                        <th className="text-left py-2 text-sm font-medium text-gray-700">Remarks</th>
                        <th className="text-center py-2 text-sm font-medium text-gray-700">Approved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settlementData.dischargeItems.map((item, index) => {
                        const qty = Number(item.quantity || 0);
                        const rate = Number(item.unitRate || (qty ? (item.billedAmount || 0) / qty : 0));
                        const eligibleRate = Number(item.eligibleUnitRate || (qty ? (item.eligibleAmount || 0) / qty : 0));
                        const billed = Number(item.billedAmount ?? item.amount ?? 0);
                        const eligible = Number(item.eligibleAmount ?? 0);
                        const variance = billed - eligible;
                        return (
                          <tr key={index} className="border-b border-gray-100">
                            <td className="py-2 text-sm text-gray-900 whitespace-normal"><ClampedText value={item.itemName || item.description} /></td>
                            <td className="py-2 text-sm text-gray-600 whitespace-normal"><ClampedText value={item.category || '-'} /></td>
                            <td className="py-2 text-sm text-gray-600 text-right">{qty}</td>
                            <td className="py-2 text-sm text-gray-600 text-right">{formatCurrency(rate)}</td>
                            <td className="py-2 text-sm text-gray-600 text-right">{formatCurrency(eligibleRate)}</td>
                            <td className="py-2 text-sm text-gray-900 text-right">{formatCurrency(billed)}</td>
                            <td className="py-2 text-sm text-gray-900 text-right">{formatCurrency(eligible)}</td>
                            <td className={`py-2 text-sm text-right ${variance > 0 ? 'text-red-600' : variance < 0 ? 'text-green-600' : 'text-gray-900'}`}>{formatCurrency(variance)}</td>
                            <td className="py-2 text-sm text-gray-600 whitespace-normal"><ClampedText value={item.remarks || '-'} /></td>
                            <td className="py-2 text-center">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.approved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {item.approved ? 'Approved' : 'Rejected'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Deductions */}
            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Deductions
                </h3>
                <button
                  onClick={addDeduction}
                  className="flex items-center space-x-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Deduction</span>
                </button>
              </div>

              {settlementData.deductions.length === 0 ? (
                <p className="text-gray-600 text-center py-4">
                  No deductions added
                </p>
              ) : (
                <div className="space-y-3">
                  {settlementData.deductions.map((deduction) => (
                    <div
                      key={deduction.id}
                      className="bg-white p-4 rounded-lg border border-red-200"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type
                          </label>
                          <select
                            value={deduction.type}
                            onChange={(e) =>
                              updateDeduction(
                                deduction.id,
                                "type",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          >
                            <option value="excess_billing">
                              Excess Billing
                            </option>
                            <option value="non_covered_items">
                              Non-Covered Items
                            </option>
                            <option value="policy_exclusion">
                              Policy Exclusion
                            </option>
                            <option value="copayment">Co-payment</option>
                            <option value="deductible">Deductible</option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <input
                            type="text"
                            value={deduction.description}
                            onChange={(e) =>
                              updateDeduction(
                                deduction.id,
                                "description",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            placeholder="Enter description"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amount
                          </label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={deduction.amount}
                              onChange={(e) =>
                                updateDeduction(
                                  deduction.id,
                                  "amount",
                                  e.target.value
                                )
                              }
                              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                              placeholder="0"
                            />
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={deduction.percentage}
                                onChange={(e) =>
                                  updateDeduction(
                                    deduction.id,
                                    "percentage",
                                    e.target.checked
                                  )
                                }
                                className="mr-1"
                              />
                              <span className="text-xs">%</span>
                            </label>
                          </div>
                        </div>

                        <div className="flex items-end">
                          <button
                            onClick={() => removeDeduction(deduction.id)}
                            className="w-full px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                          >
                            <Minus className="h-4 w-4 mx-auto" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Adjustments */}
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Adjustments
                </h3>
                <button
                  onClick={addAdjustment}
                  className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Adjustment</span>
                </button>
              </div>

              {settlementData.adjustments.length === 0 ? (
                <p className="text-gray-600 text-center py-4">
                  No adjustments added
                </p>
              ) : (
                <div className="space-y-3">
                  {settlementData.adjustments.map((adjustment) => (
                    <div
                      key={adjustment.id}
                      className="bg-white p-4 rounded-lg border border-green-200"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type
                          </label>
                          <select
                            value={adjustment.type}
                            onChange={(e) =>
                              updateAdjustment(
                                adjustment.id,
                                "type",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          >
                            <option value="emergency_allowance">
                              Emergency Allowance
                            </option>
                            <option value="compassionate_grounds">
                              Compassionate Grounds
                            </option>
                            <option value="policy_benefit">
                              Policy Benefit
                            </option>
                            <option value="goodwill_gesture">
                              Goodwill Gesture
                            </option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <input
                            type="text"
                            value={adjustment.description}
                            onChange={(e) =>
                              updateAdjustment(
                                adjustment.id,
                                "description",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            placeholder="Enter description"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amount
                          </label>
                          <input
                            type="number"
                            value={adjustment.amount}
                            onChange={(e) =>
                              updateAdjustment(
                                adjustment.id,
                                "amount",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            placeholder="0"
                          />
                        </div>

                        <div className="flex items-end">
                          <button
                            onClick={() => removeAdjustment(adjustment.id)}
                            className="w-full px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                          >
                            <Minus className="h-4 w-4 mx-auto" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Settlement Decision */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Settlement Decision
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <div className="flex space-x-4">
                    {[
                      {
                        value: "approved",
                        label: "Approve Settlement",
                        color: "text-green-600",
                      },
                      {
                        value: "pending_adjustment",
                        label: "Pending Adjustment",
                        color: "text-yellow-600",
                      },
                      {
                        value: "rejected",
                        label: "Reject Settlement",
                        color: "text-red-600",
                      },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center space-x-2"
                      >
                        <input
                          type="radio"
                          name="settlementStatus"
                          value={option.value}
                          checked={settlementData.status === option.value}
                          onChange={(e) =>
                            setSettlementData((prev) => ({
                              ...prev,
                              status: e.target.value,
                            }))
                          }
                          className="text-teal-600 focus:ring-teal-500"
                        />
                        <span className={`text-sm font-medium ${option.color}`}>
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comments & Notes
                  </label>
                  <textarea
                    value={settlementData.comments}
                    onChange={(e) =>
                      setSettlementData((prev) => ({
                        ...prev,
                        comments: e.target.value,
                      }))
                    }
                    rows={4}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Add your comments about the settlement calculation..."
                  />
                </div>
              </div>
            </div>

            {/* Final Calculation Display */}
            <div className="bg-purple-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Final Settlement Calculation
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Doctor Approved Amount:</span>
                  <span className="font-medium">
                    {formatCurrency(settlementData.approvedAmount)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-red-600">
                  <span>Less: Total Deductions:</span>
                  <span className="font-medium">
                    -{formatCurrency(totalDeductions)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-green-600">
                  <span>Add: Total Adjustments:</span>
                  <span className="font-medium">
                    +{formatCurrency(totalAdjustments)}
                  </span>
                </div>
                <div className="border-t border-gray-300 pt-3">
                  <div className="flex justify-between items-center text-lg font-bold text-purple-600">
                    <span>Final Settlement Amount:</span>
                    <span>{formatCurrency(finalAmount)}</span>
                  </div>
                </div>

                {/* Settlement Percentage */}
                <div className="text-center text-sm text-gray-600 mt-2">
                  Settlement Ratio:{" "}
                  {settlementData.approvedAmount > 0
                    ? (
                        (finalAmount / settlementData.approvedAmount) *
                        100
                      ).toFixed(1)
                    : 0}
                  % of approved amount
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={() => setCalculatorOpen(false)}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !settlementData.status}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {submitting && <RefreshCw className="h-4 w-4 animate-spin" />}
              <span>{submitting ? "Processing..." : "Process Settlement"}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded mb-4"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Discharge Settlement Calculator
          </h1>
          <p className="text-gray-600">
            Calculate final settlement amounts based on discharge summaries and
            medical reviews
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-600">
            {applications.length} applications ready
          </span>
          <button
            onClick={loadApplications}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Application ID, Patient Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Status</option>
              <option value="pending">Pending Settlement</option>
              <option value="approved">Settlement Approved</option>
              <option value="pending_adjustment">Pending Adjustment</option>
            </select>
          </div>
        </div>
      </div>

      {/* Applications List */}
      {applications.length > 0 ? (
        <div className="space-y-4">
          {applications.map((application) => (
            <ApplicationCard key={application.id} application={application} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <Calculator className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Applications Ready for Settlement
          </h3>
          <p className="text-gray-600">
            Applications need to have completed medical reviews and discharge
            summaries before settlement calculation.
          </p>
        </div>
      )}

      {/* Settlement Calculator Modal */}
      {calculatorOpen && <SettlementCalculator />}

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full h-5/6 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Final Approval Letter</h3>
                <p className="text-sm text-gray-600">Preview</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = previewUrl;
                    link.download = 'Final-Approval-Letter.pdf';
                    link.click();
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Download
                </button>
                <button
                  onClick={() => setPreviewUrl("")}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="flex-1 p-4">
              <iframe src={previewUrl} className="w-full h-full border border-gray-300 rounded" title="Letter Preview" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DischargeSettlement;
